import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import Stepper from "@/components/Stepper"
import { ConfettiModal, WarningModal } from "@/components/SubmitResultModals"
import { useSelfQuery } from "@/data/queries"
import {
  useCreateBloodGlucoseFormMutation,
  useObservationByIdQuery,
} from "@/data/queries/measurementQueries"
import { useQuickCheck, useResetQuickCheck } from "@/data/store"
import type { QuickCheckReason } from "@/model/QuickCheckReason"
import { ROUTES } from "@/routes"
import { useEffect, useState } from "react"
import { Navigate, useLocation, useNavigate } from "react-router"
import { toast } from "sonner"
import NoteScreenStep from "./steps/NoteScreenStep"
import ReasonPickerStep from "./steps/ReasonPickerStep"

const NOTE_CONTEXT = "Quick check"

const CRITICAL_CLASSIFICATIONS = new Set(["emergency-low", "critical-low", "critical-high"])

type WarningState = { reading: number | null; message: string | null }

const QuickCheckPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as { readingId?: string; initialTab?: string } | null
  const readingId = state?.readingId ?? null
  const dashboardReturnUrl = `${ROUTES.DASHBOARD}?tab=${state?.initialTab ?? "overview"}`
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""
  const { mutateAsync: createForm, isPending } = useCreateBloodGlucoseFormMutation({ patientId })
  const { data: observation } = useObservationByIdQuery({ readingId })

  const [quickCheck, setQuickCheck] = useQuickCheck()
  const resetQuickCheck = useResetQuickCheck()
  const [currentStep, setCurrentStep] = useState(0)
  const [warning, setWarning] = useState<WarningState | null>(null)
  const [confettiOpen, setConfettiOpen] = useState(false)

  useEffect(() => {
    resetQuickCheck()
  }, [resetQuickCheck])

  if (!readingId) {
    return <Navigate to={`${ROUTES.DASHBOARD}?tab=overview`} replace />
  }

  const goNext = () => setCurrentStep((step) => step + 1)

  const onReasons = (reasons: QuickCheckReason[]) => {
    setQuickCheck((current) => ({ ...current, reasons }))
    goNext()
  }

  const onNotes = async ({ notes }: { notes: string }) => {
    setQuickCheck((current) => ({ ...current, notes }))
    try {
      await createForm({
        patientId,
        form: {
          context: "quick-check",
          dateTime: null,
          notes: notes || null,
          reasons: quickCheck.reasons,
          carbsInGrams: null,
          proteinInGrams: null,
          mealContents: null,
          medications: null,
          readingId,
        },
      })
      const classification = observation?.reading?.classification
      const reading = observation?.reading?.bloodGlucoseInMilligramsPerDeciliter
      if (classification && CRITICAL_CLASSIFICATIONS.has(classification)) {
        setWarning({
          reading: reading ?? null,
          message: observation?.note ?? null,
        })
      } else {
        setConfettiOpen(true)
      }
    } catch (error) {
      const isLocked = error instanceof Error && /423/.test(error.message)
      toast.error(
        isLocked
          ? "Your doctor has already reviewed your measurement. It is no longer possible to submit the form"
          : "There was a problem submitting the form. Please try again later, or contact Bloomlife Support",
      )
    }
  }

  const dismissAndGoToDashboard = () => {
    resetQuickCheck()
    setWarning(null)
    setConfettiOpen(false)
    navigate(dashboardReturnUrl)
  }

  if (!self) {
    return (
      <Page showAppbar={false}>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Page>
    )
  }

  return (
    <Page showAppbar={false}>
      <Stepper
        currentStep={currentStep}
        steps={[
          <ReasonPickerStep
            key="reasons"
            reasons={quickCheck.reasons}
            onSubmit={onReasons}
            onBack={() => navigate(dashboardReturnUrl)}
          />,
          <NoteScreenStep
            key="notes"
            context={NOTE_CONTEXT}
            initialValues={{ notes: quickCheck.notes }}
            isLoading={isPending}
            onSubmit={onNotes}
            onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
          />,
        ]}
      />

      <WarningModal
        open={warning !== null}
        reading={warning?.reading ?? null}
        message={warning?.message ?? null}
        onClose={dismissAndGoToDashboard}
      />
      <ConfettiModal open={confettiOpen} onDismiss={dismissAndGoToDashboard} />
    </Page>
  )
}

export default QuickCheckPage
